import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeftIcon,
  SaveIcon,
  Trash2Icon,
  UndoIcon,
  MapIcon,
  AlertCircleIcon,
  CheckCircle2Icon,
} from 'lucide-react';
import {
  Map as MaplibreMap,
  Marker as MaplibreMarker,
  Popup,
  type MapMouseEvent,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useGetWorkspaceRegions, useUpdateWorkspaceRegionBoundary } from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BoundaryPoint {
  lng: number;
  lat: number;
}

export function BoundaryEditorPage() {
  const navigate = useNavigate();
  const { data: regionsData } = useGetWorkspaceRegions();
  const updateBoundary = useUpdateWorkspaceRegionBoundary();

  const regions = regionsData?.regions ?? [];
  const [selectedRegionId, setSelectedRegionId] = React.useState<string>('');
  const [points, setPoints] = React.useState<BoundaryPoint[]>([]);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [mapError, setMapError] = React.useState(false);

  const mapRef = React.useRef<HTMLDivElement>(null);
  const mapInstance = React.useRef<MaplibreMap | null>(null);
  const markersRef = React.useRef<MaplibreMarker[]>([]);

  // Initialize map
  React.useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    let map: MaplibreMap;
    try {
      map = new MaplibreMap({
        container: mapRef.current,
        style: {
          version: 8,
          sources: {
            osm: {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors',
            },
          },
          layers: [
            {
              id: 'osm-tiles',
              type: 'raster',
              source: 'osm',
            },
          ],
        },
        center: [144.9631, -37.8136],
        zoom: 12,
      });
    } catch {
      setMapError(true);
      return;
    }

    map.on('load', () => {
      // Add a source + layer for the polygon fill
      map.addSource('boundary', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [] }, properties: {} },
      });
      map.addLayer({
        id: 'boundary-fill',
        type: 'fill',
        source: 'boundary',
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.2,
        },
      });
      map.addLayer({
        id: 'boundary-line',
        type: 'line',
        source: 'boundary',
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2,
        },
      });
    });

    map.on('click', (e: MapMouseEvent) => {
      setPoints((prev) => [...prev, { lng: e.lngLat.lng, lat: e.lngLat.lat }]);
    });

    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, []);

  // Load selected region boundary
  React.useEffect(() => {
    if (!selectedRegionId) return;
    const region = regions.find((r) => r.id === selectedRegionId);
    if (!region || !region.boundaryGeojson) return;

    const geom = region.boundaryGeojson as { type: string; coordinates: number[][][] };
    if (geom.type === 'Polygon' && geom.coordinates.length > 0) {
      const ring = geom.coordinates[0]!;
      // Skip the closing point (it's a duplicate of the first)
      const pts = ring.slice(0, -1).map(([lng, lat]) => ({ lng: lng ?? 0, lat: lat ?? 0 }));
      setPoints(pts);
    }
    setError('');
    setSuccess('');
  }, [selectedRegionId, regions]);

  // Update markers when points change
  React.useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;

    // Remove old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // Add new markers
    points.forEach((pt, idx) => {
      // Build popup content with a remove button (works on touch and mouse)
      const popupContent = document.createElement('div');
      popupContent.style.cssText = 'font-family: inherit; font-size: 12px;';
      const coordsText = document.createElement('div');
      coordsText.textContent = `Vertex ${idx + 1}: ${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}`;
      popupContent.appendChild(coordsText);
      const removeBtn = document.createElement('button');
      removeBtn.textContent = 'Remove vertex';
      removeBtn.style.cssText = 'margin-top: 6px; color: #ef4444; border: none; background: none; cursor: pointer; font-size: 12px; font-weight: 600; padding: 0;';
      removeBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        setPoints((prev) => prev.filter((_, i) => i !== idx));
      });
      popupContent.appendChild(removeBtn);

      const marker = new MaplibreMarker({ color: '#3b82f6', draggable: true })
        .setLngLat([pt.lng, pt.lat])
        .setPopup(new Popup().setDOMContent(popupContent))
        .addTo(map);

      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        setPoints((prev) =>
          prev.map((p, i) => (i === idx ? { lng: lngLat.lng, lat: lngLat.lat } : p))
        );
      });

      // Right-click to remove a vertex
      marker.getElement().addEventListener('contextmenu', (e) => {
        e.preventDefault();
        setPoints((prev) => prev.filter((_, i) => i !== idx));
      });

      markersRef.current.push(marker);
    });

    // Update polygon fill
    if (points.length >= 3) {
      const closedRing = [...points.map((p) => [p.lng, p.lat]), [points[0]!.lng, points[0]!.lat]];
      const source = map.getSource('boundary') as { setData: (data: unknown) => void } | undefined;
      source?.setData({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [closedRing] },
        properties: {},
      });
    } else {
      const source = map.getSource('boundary') as { setData: (data: unknown) => void } | undefined;
      source?.setData({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [] },
        properties: {},
      });
    }
  }, [points]);

  const handleSave = async () => {
    if (!selectedRegionId || points.length < 3) {
      setError('Need at least 3 points to form a polygon.');
      return;
    }

    // Build GeoJSON polygon (closed ring)
    const closedRing = [...points.map((p) => [p.lng, p.lat]), [points[0]!.lng, points[0]!.lat]];
    const boundary = {
      type: 'Polygon' as const,
      coordinates: [closedRing],
    };

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateBoundary.mutateAsync({
        regionId: selectedRegionId,
        data: { boundaryGeojson: boundary },
      });
      setSuccess('Boundary saved successfully.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setError(msg.includes('validation')
        ? `Validation failed: ${msg}`
        : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setPoints([]);
    setError('');
    setSuccess('');
  };

  const handleUndo = () => {
    setPoints((prev) => prev.slice(0, -1));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Boundary Editor"
        icon={MapIcon}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/workspace/assets')}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Assets
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
        {/* Map */}
        <Card>
          <CardContent className="p-0">
            {mapError ? (
              <div className="flex h-[400px] flex-col items-center justify-center gap-3 rounded-lg p-6 text-center lg:h-[600px]">
                <AlertCircleIcon className="size-8 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Map unavailable</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your browser does not support WebGL or it may be disabled.
                    You can still use the controls to manage vertices.
                  </p>
                </div>
              </div>
            ) : (
              <div
                ref={mapRef}
                className="h-[400px] w-full rounded-lg lg:h-[600px]"
                style={{ cursor: 'crosshair' }}
              />
            )}
          </CardContent>
        </Card>

        {/* Controls */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Region</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={selectedRegionId} onValueChange={setSelectedRegionId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a region..." />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.id ?? ''} value={r.id ?? ''}>
                      {r.name ?? ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedRegionId && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="secondary">{points.length} vertices</Badge>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUndo}
                      disabled={points.length === 0}
                    >
                      <UndoIcon className="mr-1 h-4 w-4" />
                      Undo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClear}
                      disabled={points.length === 0}
                    >
                      <Trash2Icon className="mr-1 h-4 w-4" />
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {selectedRegionId && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Tap or click on the map to add vertices</p>
                  <p>• Drag markers to move vertices</p>
                  <p>• Tap a marker, then “Remove vertex” to delete it</p>
                  <p>• Need at least 3 points to save</p>
                </div>

                <Button
                  className="w-full"
                  onClick={handleSave}
                  disabled={saving || points.length < 3}
                >
                  <SaveIcon className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Boundary'}
                </Button>

                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    {error}
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-2 text-xs text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200">
                    <CheckCircle2Icon className="h-4 w-4" />
                    {success}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
